import { useEffect, useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { useAppSelector } from "@store/hooks";
import { getMyWorkspaceGrade } from "@services/api";
import { computeWorkspaceTotal } from "@features/workspaces/utils/gradebook";
import type { RootState } from "@store/index";

/**
 * Phase 6: a student's own "grade so far" for the current workspace —
 * the weighted mean of their released grades + bonus. Read-only, shown
 * near the workspace selector. Hidden until something is released so it
 * doesn't clutter an ungraded workspace.
 */
export default function StudentWorkspaceGrade() {
  const workspaceId = useAppSelector(
    (s: RootState) => s.workspace.workspace_id
  );
  const [points, setPoints] = useState(0);
  const [outOf, setOutOf] = useState(0);
  const [percent, setPercent] = useState<number | null>(null);
  const [graded, setGraded] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    setLoaded(false);
    getMyWorkspaceGrade(workspaceId).then((res) => {
      if (cancelled) return;
      setLoaded(true);
      if (!res.success) return; // non-critical — stay quiet
      const t = computeWorkspaceTotal(
        res.data.resources,
        res.data.override_total
      );
      setPoints(t.points);
      setOutOf(t.outOf);
      setPercent(t.percent);
      setGraded(t.gradedCount);
    });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  // Nothing released yet → don't clutter an ungraded workspace.
  if (!loaded || graded === 0) return null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 4, pb: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Your grade so far
      </Typography>
      <Chip
        label={percent != null ? `${points}/${outOf} (${percent}%)` : `${points}/${outOf}`}
        color="primary"
        size="small"
      />
      <Typography variant="caption" color="text.secondary">
        over {graded} released resource{graded === 1 ? "" : "s"}
      </Typography>
    </Box>
  );
}
